import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import {
	Table,
	Menu,
	Dropdown,
	Button,
	Icon,
	Modal,
	Typography,
	Breadcrumb,
	message,
	Avatar,
} from 'antd';

import Meta from '../../../../components/Meta';
import Fallback from '../../../../components/Fallback';
import PageSizeHandler from '../../../../components/PageSizeHandle';
import CastersAPI from '../../../../services/sdks/caster';
import { CasterNameCell, Container, TableHeader } from './styles';

import { FiPlusCircle } from 'react-icons/fi';
import ChangeCasterPassword from './ChangePassword';
import { resolveFileSrc } from '../../../../helpers/fileSrcResolver';
import env from '../../../../config/env';

const ListCasters = ({ user }) => {
	const history = useHistory();
	const [fallback, setFallback] = useState({ initialData: false });
	const [casters, setCasters] = useState([]);
	const [visibleModals, setVisibleModals] = useState({ createCaster: false });
	const [total, setTotal] = useState(0);
	const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
	const [showChangePassModal, setShowChangePassModal] = useState(false);
	const [casterId, setCasterId] = useState(null);

	const columns = [
		{
			title: 'Nome',
			key: 'name',
			render: (caster) => (
				<CasterNameCell>
					<Avatar
						size='large'
						style={{ background: 'var(--primary)' }}
						src={resolveFileSrc({ fileName: caster?.profilePic, folder: ['profile_pics'], bucket: env.miscS3Bucket })}>
						{caster?.name[0] || '-'} {caster?.surname[0] || '-'}
					</Avatar>
					<div>
						<Typography.Text className='name'>
							{caster?.name} {caster?.surname}
						</Typography.Text>
						<small>{caster.email}</small>
					</div>
				</CasterNameCell>
			),
		},
		{
			title: 'Microfone',
			dataIndex: 'mic',
			align: 'center',
			key: 'mic',
		},
		{
			title: 'Placa de Som',
			dataIndex: 'soundcard',
			align: 'center',
			key: 'soundcard',
		},
		{
			key: 'id',
			title: 'ID',
			align: 'center',
			render: (off) => (
				<Typography.Text title={off?._id} copyable={{ text: off?._id }}>{`${off?._id.slice(
					0,
					12
				)}...`}</Typography.Text>
			),
		},
		{
			title: 'Ações',
			align: 'right',
			key: 'options',
			render: (caster) => (
				<>
					<Dropdown
						placement='bottomRight'
						overlay={
							<Menu>
								<Menu.Item
									key='edit'
									onClick={() => history.push(`/commom/casters/${caster._id}/update`)}>
									<Icon type='edit' /> Editar
								</Menu.Item>
								<Menu.Item
									key='change-password'
									onClick={() => {
										setShowChangePassModal(true);
										setCasterId(caster._id);
									}}>
									<Icon type='lock' /> Alterar Senha
								</Menu.Item>

								<Menu.Divider />

								<Menu.Item
									key='delete'
									className='ant-dropdown-menu-item-danger'
									onClick={() => {
										Modal.confirm({
											title: 'Excluir off?',
											icon: 'exclamation-circle',
											content:
												'O locutor será excluído e removido de todos os programas, deseja continuar mesmo assim?',
											okText: 'Excluir',
											onOk: () => handleDeleteCaster(caster._id),
											okButtonProps: {
												icon: 'delete',
												type: 'danger',
											},
											cancelText: 'Cancelar',
											cancelButtonProps: {
												icon: 'close-circle',
											},
										});
									}}>
									<Icon type='delete' /> Excluir
								</Menu.Item>
							</Menu>
						}>
						<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 16 }} type='more' />
					</Dropdown>
				</>
			),
		},
	];

	const handleDeleteCaster = useCallback(async (casterId) => {
		try {
			await CastersAPI.destroy(casterId);
			setCasters((prev) => prev.filter((c) => c._id !== casterId));
			message.success('Locutor excluído com sucesso');
		} catch (error) {
			message.error('Houve um erro, tente novamente');
		}
	}, []);

	useEffect(() => {
		const fetchCasters = async () => {
			setFallback((prev) => ({ ...prev, initialData: true }));

			try {
				const query = `userId=${user._id}&page=${pagination.current - 1}&limit=${
					pagination.pageSize
				}`;
				const response = await CastersAPI.index(query);
				setCasters(response.data.casters);
				setTotal(response.data.total);
			} catch (error) {
				message.error('Houve um erro ao buscar os locutores');
			}

			setFallback((prev) => ({ ...prev, initialData: false }));
		};

		if (user) {
			fetchCasters();
		}
	}, [pagination, user]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<Meta title='Meus Locutores' />

			<Container>
				<Breadcrumb
					style={{ marginBottom: 12 }}
					separator='>'
					routes={[{ breadcrumbName: 'INÍCIO' }, { breadcrumbName: 'MEUS LOCUTORES' }]}
				/>

				<header>
					<Typography.Title level={2}>Meus Locutores</Typography.Title>
					<Link to='/commom/casters/create-caster'>
						<Button
							type='primary'
							onClick={() => setVisibleModals({ ...visibleModals, createCaster: true })}>
							<FiPlusCircle /> Cadastrar Locutor
						</Button>
					</Link>
				</header>

				<TableHeader>
					<div className='actions'>
						<span>
							Quantidade: <strong>{total}</strong>
						</span>
					</div>

					<PageSizeHandler pagination={pagination} setPagination={setPagination} />
				</TableHeader>

				<Table
					loading={fallback?.initialData}
					size='middle'
					rowKey='_id'
					columns={columns}
					dataSource={casters}
					style={{ border: 'none' }}
					pagination={{
						...pagination,
						size: 'large',
						onChange: (current) => setPagination({ ...pagination, current }),
					}}
				/>
			</Container>

			<ChangeCasterPassword
				casterId={casterId}
				isOpen={showChangePassModal}
				onClose={() => {
					setShowChangePassModal(false);
					setCasterId(null);
				}}
			/>
		</>
	);
};

export default connect(({ user }) => ({ user }))(ListCasters);
